import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Clock,
  AlertTriangle,
  Bell,
  Send,
  Users,
  CheckCircle,
  XCircle,
  Calendar,
  BarChart3,
  Settings,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  MessageSquare,
  History,
  TrendingUp,
  UserX,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlertesSaisieHeuresPage() {
  const [loading, setLoading] = useState(true);
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [activeTab, setActiveTab] = useState('alertes');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [configForm, setConfigForm] = useState({
    jours_sans_heures_avant_rappel: 2,
    rappel_quotidien: true,
    alerte_patron_apres_jours: 5,
    message_rappel: "Bonjour ! Vous n'avez pas saisi vos heures depuis plusieurs jours. Merci de les saisir.",
    message_urgent: "URGENT : Vos heures ne sont toujours pas saisies. Sans réponse, le dirigeant sera informé."
  });
  const [sendingRappel, setSendingRappel] = useState(null);

  const getHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': localStorage.getItem('societeId') || '',
    'Content-Type': 'application/json'
  }), []);

  const fetchAlertes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/alertes-heures/verifier`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAlertes(data.alertes || []);
      }
    } catch (error) {
      console.error('Erreur alertes:', error);
    }
  }, [getHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/alertes-heures/statistiques`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  }, [getHeaders]);

  useEffect(() => {
    Promise.all([fetchAlertes(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchAlertes, fetchStats]);

  const handleSendRappel = async (collaborateurId) => {
    setSendingRappel(collaborateurId);
    try {
      const res = await fetch(`${API_URL}/api/alertes-heures/envoyer-rappel/${collaborateurId}`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Rappel envoyé');
      } else {
        toast.error('Erreur lors de l\'envoi du rappel');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSendingRappel(null);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedCollab) return;
    try {
      const res = await fetch(`${API_URL}/api/alertes-heures/config/${selectedCollab.collaborateur_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ collaborateur_id: selectedCollab.collaborateur_id, ...configForm })
      });
      if (res.ok) {
        toast.success('Configuration sauvegardée');
        setShowConfigDialog(false);
      } else {
        toast.error('Erreur de sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const loadConfig = async (collab) => {
    setSelectedCollab(collab);
    try {
      const res = await fetch(`${API_URL}/api/alertes-heures/config/${collab.collaborateur_id}`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setConfigForm({
          jours_sans_heures_avant_rappel: data.jours_sans_heures_avant_rappel || 2,
          rappel_quotidien: data.rappel_quotidien !== false,
          alerte_patron_apres_jours: data.alerte_patron_apres_jours || 5,
          message_rappel: data.message_rappel || configForm.message_rappel,
          message_urgent: data.message_urgent || configForm.message_urgent
        });
      }
    } catch (error) {
      console.error('Erreur config:', error);
    }
    setShowConfigDialog(true);
  };

  const loadHistorique = async (collabId) => {
    try {
      const res = await fetch(`${API_URL}/api/alertes-heures/historique/${collabId}`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setHistorique(data.historique || []);
      }
    } catch (error) {
      console.error('Erreur historique:', error);
    }
  };

  const filteredAlertes = alertes.filter(a => {
    const matchSearch = !searchTerm ||
      a.collaborateur_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.collaborateur_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchNiveau = !filterNiveau || a.niveau === filterNiveau;
    return matchSearch && matchNiveau;
  });

  const getNiveauStyle = (niveau) => {
    switch (niveau) {
      case 'urgent':
        return { bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: <XCircle className="w-5 h-5 text-red-500" /> };
      case 'rappel':
        return { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: <AlertTriangle className="w-5 h-5 text-amber-500" /> };
      default:
        return { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', icon: <Clock className="w-5 h-5 text-blue-500" /> };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="alertes-saisie-heures-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl text-white">
            <Clock className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alertes Saisie des Heures</h1>
            <p className="text-gray-500">Suivi des saisies d'heures et interventions des collaborateurs</p>
          </div>
        </div>
        <Button
          onClick={() => { fetchAlertes(); fetchStats(); toast.info('Actualisation...'); }}
          variant="outline"
          data-testid="refresh-alertes-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card data-testid="stat-total-collaborateurs">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total collaborateurs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_collaborateurs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-actifs-semaine">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Actifs cette semaine</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.collaborateurs_actifs_semaine}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-taux-saisie">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taux de saisie</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.taux_saisie}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-alertes-attente">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Bell className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Alertes en attente</p>
                  <p className="text-2xl font-bold text-red-600">{stats.alertes_en_attente}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b overflow-x-auto">
        {[
          { key: 'alertes', label: 'Alertes en cours', icon: AlertTriangle },
          { key: 'historique', label: 'Historique', icon: History },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`flex items-center gap-2 px-4 py-2 font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? 'text-emerald-600 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Alertes Tab */}
      {activeTab === 'alertes' && (
        <div>
          {/* Filtres */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher un collaborateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-collaborateur"
              />
            </div>
            <Select value={filterNiveau || 'all'} onValueChange={(v) => setFilterNiveau(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-niveau">
                <SelectValue placeholder="Tous les niveaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les niveaux</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="rappel">Rappel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Liste des alertes */}
          {filteredAlertes.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border" data-testid="no-alertes">
              <CheckCircle className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Tout est en ordre</h3>
              <p className="text-gray-500">Tous les collaborateurs sont à jour dans leurs saisies</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="alertes-list">
              {filteredAlertes.map((alerte, idx) => {
                const style = getNiveauStyle(alerte.niveau);
                return (
                  <div
                    key={`${alerte.collaborateur_id}-${idx}`}
                    className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${style.bg}`}
                    data-testid={`alerte-item-${idx}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        {style.icon}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900">
                              {alerte.collaborateur_nom}
                            </h3>
                            <Badge className={style.badge}>
                              {alerte.niveau === 'urgent' ? 'Urgent' : 'Rappel'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">{alerte.collaborateur_email}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm">
                            <span className={`font-medium ${style.text}`}>
                              {alerte.jours_sans_saisie === 999
                                ? 'Jamais saisi'
                                : `${alerte.jours_sans_saisie} jours sans saisie`}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadConfig(alerte)}
                          data-testid={`config-btn-${idx}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { loadHistorique(alerte.collaborateur_id); setActiveTab('historique'); }}
                          data-testid={`historique-btn-${idx}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => handleSendRappel(alerte.collaborateur_id)}
                          disabled={sendingRappel === alerte.collaborateur_id}
                          data-testid={`send-rappel-btn-${idx}`}
                        >
                          {sendingRappel === alerte.collaborateur_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <Send className="w-4 h-4 mr-1" />
                          )}
                          Envoyer rappel
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Historique Tab */}
      {activeTab === 'historique' && (
        <div>
          {historique.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border" data-testid="no-historique">
              <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucun historique</h3>
              <p className="text-gray-500">Sélectionnez un collaborateur pour voir l'historique de ses alertes</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="historique-list">
              {historique.map((item, idx) => (
                <div key={idx} className="bg-white rounded-xl border p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <MessageSquare className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.type === 'rappel' ? 'Rappel envoyé' : item.type}</p>
                        <p className="text-sm text-gray-500 mt-1">{item.message}</p>
                        {item.reponse && (
                          <div className="mt-2 p-2 bg-emerald-50 rounded-lg">
                            <p className="text-sm text-emerald-700">
                              Raison : {item.reponse.raison}
                              {item.reponse.commentaire && ` - ${item.reponse.commentaire}`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {item.envoyee_at ? new Date(item.envoyee_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle>Configuration des alertes</DialogTitle>
            {selectedCollab && (
              <p className="text-sm text-gray-500">{selectedCollab.collaborateur_nom}</p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Jours avant premier rappel
              </label>
              <Input
                type="number"
                min={1}
                max={30}
                value={configForm.jours_sans_heures_avant_rappel}
                onChange={(e) => setConfigForm(prev => ({
                  ...prev,
                  jours_sans_heures_avant_rappel: parseInt(e.target.value) || 2
                }))}
                data-testid="config-jours-rappel"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Jours avant alerte au dirigeant
              </label>
              <Input
                type="number"
                min={1}
                max={30}
                value={configForm.alerte_patron_apres_jours}
                onChange={(e) => setConfigForm(prev => ({
                  ...prev,
                  alerte_patron_apres_jours: parseInt(e.target.value) || 5
                }))}
                data-testid="config-jours-patron"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Message de rappel
              </label>
              <Textarea
                value={configForm.message_rappel}
                onChange={(e) => setConfigForm(prev => ({ ...prev, message_rappel: e.target.value }))}
                rows={3}
                data-testid="config-message-rappel"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Message urgent
              </label>
              <Textarea
                value={configForm.message_urgent}
                onChange={(e) => setConfigForm(prev => ({ ...prev, message_urgent: e.target.value }))}
                rows={3}
                data-testid="config-message-urgent"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveConfig} data-testid="save-config-btn">
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
